import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { addForethoughtLoadingScript, removeForethoughtWidget } from '../../../lib/forethought';
import { UserService } from '../../core/services/user.service';
import { isLocalEnvironment } from '../../core/utils';
import { ID } from '../../models/id';
import { User } from '../../models/user';

declare global {
  interface Window {
    Forethought?: (type: string, action: string, payload?: unknown) => unknown;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ForethoughtWidgetService {
  private static readonly MATERIALS_PATH = '/cfo/materials';

  private router = inject(Router);
  private userService = inject(UserService);
  private isWidgetLoaded = false;
  private currentCompanyId: ID = null;

  init(): void {
    if (window.Cypress || isLocalEnvironment()) {
      return;
    }

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.handleRouteChange(event.url);
    });

    this.handleRouteChange(this.router.url);
  }

  private handleRouteChange(url: string): void {
    const user = this.userService.getUser();
    const materialsPageVisible = this.isMaterialsPage(url);

    if (this.isWidgetLoaded && this.currentCompanyId !== user?.company_id) {
      this.removeWidget();
    }

    if (materialsPageVisible && !this.isWidgetLoaded) {
      this.loadWidget(user);
    }

    if (this.isWidgetLoaded) {
      this.toggleWidgetVisibility(materialsPageVisible);
    }
  }

  private isMaterialsPage(url: string): boolean {
    const path = url.split('?')[0].split('#')[0].toLowerCase();

    if (path === ForethoughtWidgetService.MATERIALS_PATH) {
      return true;
    }

    return path.startsWith(`${ForethoughtWidgetService.MATERIALS_PATH}/`);
  }

  private loadWidget(user: User): void {
    this.currentCompanyId = user?.company_id ?? null;
    if (user?.id) {
      addForethoughtLoadingScript(user.company_id, user.first_name);
      this.isWidgetLoaded = true;
    }
  }

  private removeWidget(): void {
    removeForethoughtWidget();
    this.isWidgetLoaded = false;
  }

  private toggleWidgetVisibility(isVisible: boolean): void {
    if (isVisible) {
      this.showWidget();
    } else {
      this.closeWidget();
      this.hideWidget();
    }
  }

  private isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.Forethought === 'function';
  }

  openWidget(): void {
    if (this.isAvailable()) {
      window.Forethought('widget', 'open');
    }
  }

  closeWidget(): void {
    if (this.isAvailable()) {
      window.Forethought('widget', 'close');
    }
  }

  showWidget(): void {
    if (this.isAvailable()) {
      window.Forethought('widget', 'show');
    }
  }

  hideWidget(): void {
    if (this.isAvailable()) {
      window.Forethought('widget', 'hide');
    }
  }
}
